import { useEffect, useMemo, useState } from "react";
import { DatePicker } from "@material-ui/pickers";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

import { useSearchContext } from "../../../Toolbar/SearchContext";
import { Modal } from "../../../../../../_metronic/elements/Modal";
import { KTSVG } from "../../../../../../_metronic/helpers";
import { Loading } from "../../../../../../_metronic/elements/Loading";
import { useCreateNDVIReportContext, CreateNDVIReportProvider } from "./CreateNDVIReportContext";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../../../../shared/responsive";
import { formatFixedNumber } from "../../../../../shared/numberFormat";
import {
  getLandDates,
  getLandHdAccess,
  searchLandHdAcquisitions,
} from "../../../Fields/services/GisDataAdapter";
import "../../../Fields/ViewField/ViewLand/Images/DateSelector.style.scss";
import { PLANET_HD_V1_ENABLED } from "../../../../../config/featureFlags";
import { canUsePlanetHd } from "../../../Fields/ViewField/ViewLand/Images/planetHdAccessModel";
import {
  REMOTE_SENSING_DATE_STATES,
  normalizeRemoteSensingDateItems,
} from "../../../Fields/ViewField/ViewLand/Images/remoteSensingDateModel";
import {
  REMOTE_SENSING_PRODUCTS,
  normalizeRemoteSensingSource,
} from "../../../Fields/ViewField/ViewLand/Images/remoteSensingModel";
import {
  buildPlanetHdAcquisitionQuery,
  isPlanetHdDateAllowed,
  toPlanetHdDateValue,
} from "../../../Fields/ViewField/ViewLand/Images/planetHdAcquisitionQuery";
import { selectBestPlanetHdItemsByDate } from "../../../Fields/ViewField/ViewLand/Images/planetHdCalendarModel";

const DEFAULT_CLOUDINESS_FILTER = 20;
const MAX_HD_REPORT_SCENES = 60;

const sortDatesDescending = (dates = []) =>
  [...dates].sort((left, right) => new Date(right) - new Date(left));

const FormHeader = ({ mobile = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (mobile) {
    return (
      <div className="reports-mobile-card__header">
        <button
          type="button"
          className="btn btn-sm btn-light reports-mobile-card__back"
          onClick={() => navigate("/gis/reports/ndvi")}
        >
          <KTSVG
            path="/media/icons/duotune/arrows/arr063.svg"
            className="svg-icon svg-icon-2"
          />
          <span>{t("common:navigation.back_reports")}</span>
        </button>
        <div className="reports-mobile-card__eyebrow">{t("reports:selector.report_label")}</div>
        <h2 className="reports-mobile-card__title">{t("reports:ndvi.create.title")}</h2>
      </div>
    );
  }

  return (
    <div className="modal-header">
      <h5 className="modal-title">{t("reports:ndvi.create.title")}</h5>
    </div>
  );
};

const FormFooter = ({ mobile = false, onClose }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loading, onSubmit } = useCreateNDVIReportContext();

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }

    if (mobile) {
      navigate("/gis/reports/ndvi");
      return;
    }
  };

  return (
    <div className={mobile ? "reports-mobile-form__footer" : "modal-footer"}>
      <button type="button" className="btn btn-light" onClick={handleClose}>
        {t("reports:ndvi.create.buttons.cancel")}
      </button>
      <button onClick={onSubmit} disabled={loading} type="button" className="btn btn-primary">
        {t("reports:ndvi.create.buttons.create")}
      </button>
    </div>
  );
};

const Land = ({ field, land, mobile = false }) => {
  const { selected, updateLandSelected } = useCreateNDVIReportContext();

  return (
    <label className={mobile ? "reports-mobile-land-item" : "d-flex align-items-center p-5 land-item cursor-pointer"}>
      <span className={mobile ? "reports-mobile-land-item__copy" : "flex-grow-1"}>
        <span className={mobile ? "reports-mobile-land-item__name" : "text-gray-800 text-hover-primary fw-bold fs-6"}>
          {land.name}
        </span>
      </span>
      <input
        type="checkbox"
        checked={!!selected[land.id]}
        onChange={() => updateLandSelected(field, land)}
      />
    </label>
  );
};

export const ReportField = ({ field, mobile = false }) => {
  const { t } = useTranslation();
  const { selected, updateFieldSelected } = useCreateNDVIReportContext();
  const [open, setOpen] = useState(false);
  const allSelected = field.lands.every((l) => selected[l.id]);

  if (mobile) {
    return (
      <div className="reports-mobile-field-group">
        <button
          type="button"
          className="reports-mobile-field-group__header"
          onClick={() => setOpen((current) => !current)}
        >
          <span className="reports-mobile-field-group__copy">
            <span className="reports-mobile-field-group__title">{field.name}</span>
            <span className="reports-mobile-field-group__meta">{field.lands.length} lotes</span>
          </span>
          <span className="reports-mobile-field-group__actions">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(event) => {
                event.stopPropagation();
                updateFieldSelected(field);
              }}
              onClick={(event) => event.stopPropagation()}
            />
            <KTSVG
              path={open ? "/media/icons/duotune/arrows/arr072.svg" : "/media/icons/duotune/arrows/arr071.svg"}
              className="svg-icon svg-icon-2"
            />
          </span>
        </button>
        {open && (
          <div className="reports-mobile-field-group__body">
            {field.lands.map((land) => (
              <Land key={land.id} field={field} land={land} mobile />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="accordion-item">
      <h2
        className="accordion-header reports-desktop-field-group"
        id={"kt_accordion_1_header_1" + field.field_id}
        onClick={() => setOpen((current) => !current)}
      >
        <button
          className={`reports-desktop-field-group__button ${open ? "" : "collapsed"}`}
          type="button"
          aria-expanded={open}
          aria-controls={"kt_accordion_1_body_1" + field.field_id}
        >
          <span>{field.name}</span>
        </button>
        <label
          className="form-check form-switch form-check-custom form-check-solid reports-desktop-field-group__select-all"
          onClick={(event) => event.stopPropagation()}
        >
          <input
            className="form-check-input"
            type="checkbox"
            aria-label={t("reports:technical.form.scope.select_field")}
            checked={allSelected}
            onChange={(event) => {
              event.stopPropagation();
              updateFieldSelected(field);
            }}
            id={`select-field-${field.field_id}`}
          />
        </label>
      </h2>
      <div
        id={"kt_accordion_1_body_1" + field.field_id}
        className={`accordion-collapse collapse ${open ? "show" : ""}`}
        aria-labelledby={"kt_accordion_1_header_1" + field.field_id}
      >
        <div className="accordion-body p-0">
          {field.lands.map((land) => {
            return <Land key={land.id} field={field} land={land} />;
          })}
        </div>
      </div>
    </div>
  );
};

const ListLands = ({ fields, mobile = false }) => {
  const { t } = useTranslation();
  const fieldsFiltered = useMemo(() => fields.filter((field) => field.lands.length >= 1), [fields]);

  if (mobile) {
    return (
      <div className="reports-mobile-field-groups">
        {fieldsFiltered.map((field) => (
          <ReportField key={field.field_id} field={field} mobile />
        ))}
        {fieldsFiltered.length < 1 && <p className="mb-0">{t("reports:ndvi.create.errors.lands_not_exists")}</p>}
      </div>
    );
  }

  return (
    <div className="accordion" id="kt_accordion_1">
      {fieldsFiltered.map((field) => {
        return <ReportField key={field.field_id} field={field} />;
      })}
      {fieldsFiltered.length < 1 && <p>{t("reports:ndvi.create.errors.lands_not_exists")}</p>}
    </div>
  );
};

const DateSelector = ({
  clearDates = [],
  cloudyDates = [],
  hdOnly = false,
  date,
  disabled = false,
  setDate,
  dateText,
  mobile = false,
}) => {
  const [openedDatePicker, setOpenedDatePicker] = useState(false);
  const { t } = useTranslation();
  const clearDateSet = useMemo(() => new Set(clearDates), [clearDates]);
  const cloudyDateSet = useMemo(() => new Set(cloudyDates), [cloudyDates]);
  const triggerClassName = mobile
    ? `reports-mobile-date-trigger ${disabled ? "is-disabled" : ""}`.trim()
    : `btn btn-link p-0 fw-bold text-gray-800 text-hover-primary fs-7 reports-ndvi-date-trigger ${disabled ? "is-disabled" : ""}`.trim();
  const renderCalendarDay = (day, pickerSelectedDate, dayInCurrentMonth, dayComponent) => {
    const dateValue = format(day, "yyyy-MM-dd");
    const isClear = clearDateSet.has(dateValue);
    const isCloudy = cloudyDateSet.has(dateValue);
    const isSelected = date ? format(date, "yyyy-MM-dd") === dateValue : false;
    const dateClass = isCloudy ? "date-with-cloud" : isClear ? "date-with-data" : "";
    const activeClass = isSelected ? "active" : "";
    const tooltipMessage = isCloudy
      ? `Nubosidad mayor a ${DEFAULT_CLOUDINESS_FILTER}%`
      : isClear
        ? `Nubosidad menor o igual a ${DEFAULT_CLOUDINESS_FILTER}%`
        : "Sin imagen disponible";

    return (
      <div className={`${dateClass} ${activeClass}`.trim()} title={tooltipMessage}>
        {dayComponent}
      </div>
    );
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setOpenedDatePicker(true);
          }
        }}
        className={triggerClassName}
      >
        <KTSVG
          path="/media/icons/duotune/general/gen014.svg"
          className="svg-icon svg-icon-2x svg-icon-primary me-3"
        />
        <span>
          {!date || date == null
            ? dateText
            : format(date, "dd/MM/yyyy")}
        </span>
      </button>
      <DatePicker
        open={!disabled && openedDatePicker}
        value={date}
        autoOk
        okLabel={null}
        cancelLabel={t("common:actions.cancel")}
        onOpen={() => {
          if (!disabled) {
            setOpenedDatePicker(true);
          }
        }}
        onClose={() => setOpenedDatePicker(false)}
        onChange={(value) => {
          setDate(value);
          setOpenedDatePicker(false);
        }}
        renderDay={renderCalendarDay}
        shouldDisableDate={(currentDate) => currentDate > new Date() || (hdOnly && !isPlanetHdDateAllowed(currentDate))}
        animateYearScrolling
        DialogProps={{
          maxWidth: false,
          PaperProps: {
            className: "view-land-date-picker-dialog",
          },
        }}
        TextFieldComponent={() => null}
      />
    </>
  );
};

const FormBody = ({ fields, mobile = false }) => {
  const { t } = useTranslation();
  const {
    selected,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    name,
    setName,
    product,
    setProduct,
    setHdReadiness,
  } = useCreateNDVIReportContext();
  const [search, setSearch] = useState("");
  const [filteredFields, setFilteredFields] = useState(JSON.parse(JSON.stringify(fields)));
  const [imageDateState, setImageDateState] = useState({
    clearDates: [],
    cloudyDates: [],
    status: "idle",
  });
  const selectedLandIds = useMemo(
    () => Object.keys(selected).filter((landId) => selected[landId]).sort(),
    [selected]
  );
  const selectedLandIdsKey = useMemo(() => selectedLandIds.join("|"), [selectedLandIds]);
  const hdDateRangeKey = product === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD
    ? `${toPlanetHdDateValue(fromDate) || ""}:${toPlanetHdDateValue(toDate) || ""}`
    : "";
  const totalHa = useMemo(() => {
    let total = 0;
    const indexedLand = {};
    fields.forEach((field) => {
      field.lands.forEach((land) => {
        indexedLand[land.id] = land;
      });
    });
    Object.keys(selected).forEach((landId) => {
      if (selected[landId] && indexedLand[landId]) {
        total += parseFloat(indexedLand[landId].ha);
      }
    });
    return formatFixedNumber(total);
  }, [fields, selected]);

  useEffect(() => {
    if (search && search.length >= 1) {
      const searchSplitted = search.toLowerCase().split(" ");
      const nextFields = JSON.parse(JSON.stringify(fields))
        .filter((field) => {
          const fieldName = field.name.toLowerCase();
          const stringField = JSON.stringify(field).toLowerCase();
          return searchSplitted.some((word) => fieldName.indexOf(word) !== -1 || stringField.indexOf(word) !== -1);
        })
        .map((field) => {
          field.lands = field.lands.filter((land) => {
            return searchSplitted.some((word) => land.name.toLowerCase().indexOf(word) !== -1);
          });
          return field;
        });
      setFilteredFields(nextFields);
      return;
    }

    setFilteredFields(fields);
  }, [search, fields]);

  useEffect(() => {
    let cancelled = false;
    const landIds = selectedLandIdsKey ? selectedLandIdsKey.split("|") : [];
    const [hdDateFrom, hdDateTo] = hdDateRangeKey.split(":");

    if (landIds.length < 1) {
      setImageDateState({
        clearDates: [],
        cloudyDates: [],
        status: "idle",
      });
      setHdReadiness("idle");
      return () => {
        cancelled = true;
      };
    }

    setImageDateState((current) => ({
      ...current,
      status: "loading",
    }));

    if (
      product === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD &&
      !PLANET_HD_V1_ENABLED
    ) {
      setHdReadiness("feature_disabled");
      setImageDateState({
        clearDates: [],
        cloudyDates: [],
        status: "feature_disabled",
      });
      return () => {
        cancelled = true;
      };
    }

    if (
      PLANET_HD_V1_ENABLED &&
      product === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD
    ) {
      setHdReadiness("loading");
      const source = normalizeRemoteSensingSource({
        product,
        layer: "ndvi",
      });
      const requests = landIds.map(async (landId) => {
        const accessResponse = await getLandHdAccess(landId, {
          operation: "ndvi_report_hd",
          product,
        });
        const access = accessResponse?.ok ? accessResponse.data : null;
        if (!canUsePlanetHd(access)) {
          return {accessReady: false, items: []};
        }

        const acquisitionResponse = await searchLandHdAcquisitions(landId, {
          ...buildPlanetHdAcquisitionQuery({
            cloudPercent: DEFAULT_CLOUDINESS_FILTER,
            dateFrom: hdDateFrom,
            dateTo: hdDateTo,
          }),
        });
        if (!acquisitionResponse?.ok) {
          throw new Error("Planet HD acquisitions could not be loaded");
        }
        return {
          accessReady: true,
          items: normalizeRemoteSensingDateItems(
            acquisitionResponse.data,
            source
          ).filter((item) => [
            REMOTE_SENSING_DATE_STATES.READY,
            REMOTE_SENSING_DATE_STATES.CANDIDATE,
            REMOTE_SENSING_DATE_STATES.PREPARING,
          ].includes(item.status)),
        };
      });

      Promise.allSettled(requests).then((results) => {
        if (cancelled) {
          return;
        }

        if (results.some((result) => result.status === "rejected")) {
          setHdReadiness("error");
          setImageDateState({clearDates: [], cloudyDates: [], status: "error"});
          return;
        }

        const values = results.map((result) => ({
          ...result.value,
          items: selectBestPlanetHdItemsByDate(result.value.items),
        }));
        if (values.some((value) => !value.accessReady)) {
          setHdReadiness("access_not_ready");
          setImageDateState({
            clearDates: [],
            cloudyDates: [],
            status: "access_not_ready",
          });
          return;
        }

        const eligibleCounts = values.map(({items}) => items.filter(
          (item) => item.cloudPercent != null && item.cloudPercent <= 5
        ).length);
        const usableScenes = eligibleCounts.reduce((count, itemCount) => count + itemCount, 0);
        const readiness = eligibleCounts.some((itemCount) => itemCount === 0)
          ? "no_scenes"
          : usableScenes > MAX_HD_REPORT_SCENES
            ? "too_many_scenes"
            : "ready";

        const clearDates = new Set();
        const cloudyDates = new Set();
        values.forEach(({items}) => {
          items.forEach((item) => {
            const target = item.cloudPercent != null && item.cloudPercent > DEFAULT_CLOUDINESS_FILTER
              ? cloudyDates
              : clearDates;
            target.add(item.date);
          });
        });
        setHdReadiness(readiness);
        setImageDateState({
          clearDates: sortDatesDescending(
            Array.from(clearDates).filter((dateValue) => !cloudyDates.has(dateValue))
          ),
          cloudyDates: sortDatesDescending(Array.from(cloudyDates)),
          status: readiness,
        });
      });

      return () => {
        cancelled = true;
      };
    }

    setHdReadiness("ready");

    const requests = landIds.flatMap((landId) => [
      getLandDates(landId, {
        cloud_coverage_filter: "below",
        cloud_coverage: DEFAULT_CLOUDINESS_FILTER,
      }).then((res) => ({
        dates: res?.ok ? res.data : [],
        type: "clear",
      })),
      getLandDates(landId, {
        cloud_coverage_filter: "above",
        cloud_coverage: DEFAULT_CLOUDINESS_FILTER,
      }).then((res) => ({
        dates: res?.ok ? res.data : [],
        type: "cloudy",
      })),
    ]);

    Promise.allSettled(requests)
      .then((results) => {
        if (cancelled) {
          return;
        }

        const clearDates = new Set();
        const cloudyDates = new Set();

        results.forEach((result) => {
          if (result.status !== "fulfilled") {
            return;
          }

          const target = result.value.type === "clear" ? clearDates : cloudyDates;
          (result.value.dates || []).forEach((dateValue) => {
            target.add(dateValue);
          });
        });

        setImageDateState({
          clearDates: sortDatesDescending(
            Array.from(clearDates).filter((dateValue) => !cloudyDates.has(dateValue))
          ),
          cloudyDates: sortDatesDescending(Array.from(cloudyDates)),
          status: "ready",
        });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setImageDateState({
          clearDates: [],
          cloudyDates: [],
          status: "error",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [hdDateRangeKey, product, selectedLandIdsKey, setHdReadiness]);

  const dateSelectionDisabled = selectedLandIds.length < 1 || !["ready", "no_scenes", "too_many_scenes"].includes(imageDateState.status);

  return (
    <div className={mobile ? "reports-mobile-form" : "modal-body reports-desktop-form"}>
      <section className={mobile ? "reports-mobile-form__section" : ""}>
        <p className="fw-bold fs-5">{t("reports:ndvi.create.inputs.name.label")}</p>
        <input
          type="text"
          className="form-control form-control-solid mb-3"
          placeholder={t("reports:ndvi.create.inputs.name.placeholder")}
          value={name}
          maxLength={32}
          onChange={(e) => setName(e.target.value)}
        />
      </section>

      {PLANET_HD_V1_ENABLED && (
        <section className={mobile ? "reports-mobile-form__section" : "mb-5"}>
          <p className="fw-bold fs-5">
            {t("reports:ndvi.create.inputs.product.title")}
          </p>
          <div className="d-flex flex-wrap gap-2" role="group">
            {[
              REMOTE_SENSING_PRODUCTS.SENTINEL,
              REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD,
            ].map((option) => (
              <button
                type="button"
                key={option}
                className={`btn btn-sm ${product === option ? "btn-primary" : "btn-light-primary"}`}
                onClick={() => setProduct(option)}
              >
                {option === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD
                  ? t("images:product.planet_hd")
                  : t("images:product.sentinel")}
              </button>
            ))}
          </div>
          <small className="text-muted d-block mt-2">
            {product === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD
              ? t("reports:ndvi.create.inputs.product.hd_help")
              : t("reports:ndvi.create.inputs.product.sentinel_help")}
          </small>
        </section>
      )}

      <section className={mobile ? "reports-mobile-form__section" : ""}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <p className="fw-bold fs-5 mb-0">{t("reports:ndvi.create.inputs.lands.title")}</p>
          <span className="badge badge-light-primary fs-7 fw-bold">{totalHa} ha</span>
        </div>
        <input
          type="text"
          className="form-control form-control-solid mb-3"
          placeholder="Buscar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <ListLands fields={filteredFields} mobile={mobile} />
      </section>

      <section className={mobile ? "reports-mobile-form__section reports-ndvi-date-section" : "reports-ndvi-date-section"}>
        <p className="fw-bold fs-5">{t("reports:ndvi.create.inputs.dates.title")}</p>
        {selectedLandIds.length < 1 && (
          <small className="reports-ndvi-date-hint">
            {t("reports:ndvi.create.inputs.dates.select_lands_first")}
          </small>
        )}
        {selectedLandIds.length > 0 && imageDateState.status === "loading" && (
          <small className="reports-ndvi-date-hint reports-ndvi-date-hint--loading">
            <span>{t("reports:ndvi.create.inputs.dates.loading_dates")}</span>
            <span className="spinner-border spinner-border-sm" />
          </small>
        )}
        {imageDateState.status === "access_not_ready" && (
          <small className="reports-ndvi-date-hint text-danger">
            {t("reports:ndvi.create.inputs.dates.hd_access_not_ready")}
          </small>
        )}
        {imageDateState.status === "error" && (
          <small className="reports-ndvi-date-hint text-danger">
            {t("reports:ndvi.create.inputs.dates.hd_load_error")}
          </small>
        )}
        {imageDateState.status === "no_scenes" && (
          <small className="reports-ndvi-date-hint text-danger">
            {t("reports:ndvi.create.inputs.dates.hd_no_scenes")}
          </small>
        )}
        {imageDateState.status === "too_many_scenes" && (
          <small className="reports-ndvi-date-hint text-danger">
            {t("reports:ndvi.create.inputs.dates.hd_too_many_scenes")}
          </small>
        )}
        <div className={mobile ? "reports-mobile-date-grid" : "row mb-5"}>
          <div className={mobile ? "" : "col-6"}>
            <DateSelector
              clearDates={imageDateState.clearDates}
              hdOnly={product === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD}
              cloudyDates={imageDateState.cloudyDates}
              disabled={dateSelectionDisabled}
              mobile={mobile}
              date={fromDate}
              setDate={setFromDate}
              dateText={t("reports:ndvi.create.inputs.dates.from_date")}
            />
          </div>
          <div className={mobile ? "" : "col-6"}>
            <DateSelector
              clearDates={imageDateState.clearDates}
              hdOnly={product === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD}
              cloudyDates={imageDateState.cloudyDates}
              disabled={dateSelectionDisabled}
              mobile={mobile}
              date={toDate}
              setDate={setToDate}
              dateText={t("reports:ndvi.create.inputs.dates.to_date")}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

const CreateReportContent = ({ mobile = false, onClose }) => {
  const { t } = useTranslation();
  const { groupedOptions } = useSearchContext();
  const { loading } = useCreateNDVIReportContext();

  if (loading) {
    return (
      <div className={mobile ? "reports-mobile-card__body" : "p-5"}>
        <Loading loadingText={t("reports:ndvi.create.creating")} />
      </div>
    );
  }

  if (mobile) {
    return (
      <>
        <FormHeader mobile />
        <div className="reports-mobile-card__body reports-mobile-card__body--form">
          <FormBody fields={groupedOptions} mobile />
          <FormFooter mobile onClose={onClose} />
        </div>
      </>
    );
  }

  return (
    <>
      <FormHeader />
      <FormBody fields={groupedOptions} />
      <FormFooter onClose={onClose} />
    </>
  );
};

const CreateNDVIReportContainer = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <CreateNDVIReportProvider>
        <div className="reports-mobile-shell displayable">
          <div className="card shadow-sm right-card-data reports-mobile-card reports-mobile-card--editor">
            <CreateReportContent mobile onClose={() => navigate("/gis/reports/ndvi")} />
          </div>
        </div>
      </CreateNDVIReportProvider>
    );
  }

  return (
    <CreateNDVIReportProvider>
      <Modal open={true} setOpen={() => navigate("/gis/reports/ndvi")} focus={false}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <CreateReportContent onClose={() => navigate("/gis/reports/ndvi")} />
          </div>
        </div>
      </Modal>
    </CreateNDVIReportProvider>
  );
};

export default CreateNDVIReportContainer;
